import React, { useEffect, useMemo, useState, useRef } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { db, auth } from "../firebase";
import { Timestamp, collection, doc, getDoc, getDocs, onSnapshot, updateDoc, setDoc, query, orderBy, deleteDoc, QueryDocumentSnapshot } from "firebase/firestore";
import "../styles/Modal.css";
import "../styles/TaskBoard.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { logUserAction } from "../utils/logUtils";
import TaskModal from "../components/TaskModal";

interface SubTask {
  id: string;
  title: string;
  completed: boolean;
  assignedTo?: string;
  status?: string;
  completedAt?: Timestamp;
  order?: number; // Ordre dans la liste pour le réordonnancement
}

interface Task {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  assignedTo?: string;
  dueDate?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  completedAt?: Timestamp;
  uid?: string;
  listId?: string; // ID de la liste/colonne TaskBoard
  completed?: boolean;
  completedItems?: number;
  totalItems?: number;
  subTasks?: SubTask[];
  subTaskMode?: "Collectif" | "Individuel";
  order?: number; // Ordre dans la liste pour le réordonnancement
}

interface TaskBoardList {
  id: string;
  title: string;
  color: 'orange' | 'green' | 'grey' | 'blue' | 'purple';
}

const TachesAdministration: React.FC = () => {
  // États
  const [tasksList, setTasksList] = useState<Task[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [taskToView, setTaskToView] = useState<Task | null>(null);
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [draggedOverList, setDraggedOverList] = useState<string | null>(null);
  const [draggedOverTask, setDraggedOverTask] = useState<string | null>(null);
  const [draggedSubTask, setDraggedSubTask] = useState<{task: Task, subTask: SubTask} | null>(null);
  const [draggedOverSubTask, setDraggedOverSubTask] = useState<string | null>(null);
  const [draggedOverTaskForSubTask, setDraggedOverTaskForSubTask] = useState<string | null>(null);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  // Initialiser avec toutes les listes sauf "today" en mode vertical
  const [verticalLists, setVerticalLists] = useState<Set<string>>(new Set(['this-week', 'later']));
  // Liste temporairement en mode horizontal pendant le drag
  const [temporarilyHorizontalList, setTemporarilyHorizontalList] = useState<string | null>(null);
  // Ref pour suivre si un drop réussi a eu lieu (pour éviter que handleDragEnd ne réinitialise temporarilyHorizontalList)
  const successfulDropRef = useRef<boolean>(false);
  // État pour afficher le GIF de célébration
  const [showCelebrationGif, setShowCelebrationGif] = useState(false);
  // Ref pour stocker l'état précédent des tâches (pour détecter les changements)
  const previousTasksStateRef = useRef<Map<string, boolean>>(new Map());
  // Ref pour savoir si c'est le premier chargement (pour éviter de déclencher au chargement initial)
  const isInitialLoadRef = useRef<boolean>(true);
  // Ref pour savoir si l'initialisation est terminée (après un délai)
  const initializationCompleteRef = useRef<boolean>(false);
  // Ref pour l'objet Audio (préchargé)
  const celebrationSoundRef = useRef<HTMLAudioElement | null>(null);
  const [lists, setLists] = useState<TaskBoardList[]>([
    { id: 'today', title: 'Aujourd\'hui', color: 'orange' },
    { id: 'this-week', title: 'Cette semaine', color: 'green' },
    { id: 'later', title: 'Plus tard', color: 'grey' }
  ]);
  const [showAddListModal, setShowAddListModal] = useState(false);
  const [newListTitle, setNewListTitle] = useState("");
  const [newListColor, setNewListColor] = useState<'orange' | 'green' | 'grey' | 'blue' | 'purple'>('blue');
  const [showEditListModal, setShowEditListModal] = useState(false);
  const [listToEdit, setListToEdit] = useState<TaskBoardList | null>(null);
  const [editListTitle, setEditListTitle] = useState("");
  const [editListColor, setEditListColor] = useState<'orange' | 'green' | 'grey' | 'blue' | 'purple'>('blue');

  // Titre de la page
  usePageTitle("Planner");

  // Chargement des listes depuis Firestore
  useEffect(() => {
    const docRef = doc(db, "administration", "taskboard-lists");
    const unsubscribe = onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        if (data.lists && Array.isArray(data.lists)) {
          // Merger les listes Firestore avec les listes par défaut pour s'assurer qu'elles sont toujours présentes
          const defaultListIds = ['today', 'this-week', 'later'];
          const defaultListsState = [
            { id: 'today', title: 'Aujourd\'hui', color: 'orange' },
            { id: 'this-week', title: 'Cette semaine', color: 'green' },
            { id: 'later', title: 'Plus tard', color: 'grey' }
          ];
          
          // Récupérer les listes par défaut existantes ou utiliser celles du state
          const existingDefaultLists = data.lists.filter((list: TaskBoardList) => defaultListIds.includes(list.id));
          const mergedDefaultLists = defaultListIds.map(id => {
            const existing = existingDefaultLists.find((list: TaskBoardList) => list.id === id);
            return existing || defaultListsState.find(list => list.id === id)!;
          });
          
          // Combiner les listes par défaut avec les listes personnalisées
          const customLists = data.lists.filter((list: TaskBoardList) => !defaultListIds.includes(list.id));
          const allLists = [...mergedDefaultLists, ...customLists];
          setLists(allLists);
          
          // Mettre à jour verticalLists pour s'assurer que toutes les listes sauf "today" sont en mode vertical
          setVerticalLists(prev => {
            const newSet = new Set<string>();
            allLists.forEach(list => {
              if (list.id !== 'today') {
                newSet.add(list.id);
              }
            });
            return newSet;
          });
        }
      } else {
        // Si le document n'existe pas, garder les listes par défaut dans le state
        // Les listes par défaut sont déjà dans le state initial
        // S'assurer que verticalLists est correctement initialisé
        setVerticalLists(new Set(['this-week', 'later']));
      }
    }, (error) => {
      console.error("Erreur lors du chargement des listes:", error);
    });

    return () => unsubscribe();
  }, []);

  // Gestion du dragEnd global pour réinitialiser les états si le drag est annulé
  useEffect(() => {
    const handleDragEnd = () => {
      // Réinitialiser les états après un délai pour permettre aux drops de se terminer
      // Les drops réussis réinitialisent déjà les états dans leur finally
      const currentDraggedSubTask = draggedSubTask;
      setTimeout(() => {
        // Vérifier si draggedSubTask est toujours le même (pas de drop réussi)
        if (currentDraggedSubTask && draggedSubTask === currentDraggedSubTask) {
          // Drag annulé - réinitialiser tous les états
          setDraggedSubTask(null);
          setDraggedOverSubTask(null);
          setDraggedOverTaskForSubTask(null);
          setDraggedOverList(null);
          setDraggedOverTask(null);
          // Réinitialiser temporarilyHorizontalList seulement si le drag a été annulé
          // (pas de drop réussi, donc on remet tout en vertical)
          if (!successfulDropRef.current) {
            setTemporarilyHorizontalList(null);
          }
        }
        // Si draggedSubTask a été réinitialisé (drop réussi), ne pas toucher à temporarilyHorizontalList
        // car il a été géré dans handleSubTaskDropOnTask ou handleSubTaskDrop
        // Mais réinitialiser draggedOverList et draggedOverTask pour éviter le bug "Déposer ici"
        if (!currentDraggedSubTask || draggedSubTask !== currentDraggedSubTask) {
          // Drop réussi - s'assurer que draggedOverList et draggedOverTask sont réinitialisés
          setDraggedOverList(null);
          setDraggedOverTask(null);
        }
      }, 600);
    };

    document.addEventListener('dragend', handleDragEnd);
    return () => {
      document.removeEventListener('dragend', handleDragEnd);
    };
  }, [draggedSubTask]);

  // Précharger le son de célébration
  useEffect(() => {
    celebrationSoundRef.current = new Audio('/item_obtained.mp3');
    celebrationSoundRef.current.preload = 'auto';
    celebrationSoundRef.current.volume = 0.5; // Volume à 50%
    
    return () => {
      if (celebrationSoundRef.current) {
        celebrationSoundRef.current.pause();
        celebrationSoundRef.current = null;
      }
    };
  }, []);

  // Chargement des tâches depuis Firestore
  useEffect(() => {
    const tasksCollection = collection(db, "Taches");
    const q = query(tasksCollection, orderBy("createdAt", "desc"));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allTasks: Task[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        // Initialiser l'ordre des sous-tâches si manquant
        const subTasks = data.subTasks ? data.subTasks.map((st: SubTask, index: number) => ({
          ...st,
          order: st.order !== undefined && st.order !== null ? st.order : index
        })) : undefined;
        
        allTasks.push({
          ...data,
          uid: data.uid || docSnap.id, // Utiliser l'uid du document ou l'ID du document comme fallback
          order: data.order !== undefined && data.order !== null ? data.order : 0, // Initialiser order à 0 si manquant
          subTasks: subTasks
        } as Task);
      });
      setTasksList(allTasks);
    }, (error) => {
      console.error("Erreur lors du chargement des tâches:", error);
      toast.error("Erreur lors du chargement des tâches");
    });

    return () => unsubscribe();
  }, []);

  // Initialiser l'état précédent après le chargement initial (une seule fois)
  useEffect(() => {
    if (tasksList.length > 0 && isInitialLoadRef.current && !initializationCompleteRef.current) {
      // Attendre un délai pour s'assurer que toutes les données sont chargées et stabilisées
      const initTimeout = setTimeout(() => {
        // Initialiser l'état précédent avec l'état actuel de toutes les tâches
        tasksList.forEach((task) => {
          // Calculer si la carte est terminée
          let allSubTasksCompleted = false;
          if (task.subTasks && task.subTasks.length > 0) {
            const completedCount = task.subTasks.filter(st => st.status === 'Terminée').length;
            const totalCount = task.subTasks.length;
            allSubTasksCompleted = totalCount > 0 && completedCount === totalCount;
          }
          
          const isTaskCompleted = task.status === 'Terminée' && (!task.subTasks || task.subTasks.length === 0 || allSubTasksCompleted);
          
          // Identifier la tâche de manière unique
          const taskId = task.uid || task.id || `${task.title}_${task.createdAt?.toMillis()}`;
          
          // Stocker l'état actuel comme état précédent (sans déclencher)
          previousTasksStateRef.current.set(taskId, isTaskCompleted);
        });
        
        // Marquer que l'initialisation est terminée
        isInitialLoadRef.current = false;
        initializationCompleteRef.current = true;
      }, 2000); // Attendre 2 secondes pour que les données se stabilisent
      
      return () => clearTimeout(initTimeout);
    }
  }, [tasksList]);

  // Détecter quand une carte passe à "Terminée" et déclencher le GIF + son
  useEffect(() => {
    // Ne rien faire si l'initialisation n'est pas terminée ou si tasksList est vide
    if (!initializationCompleteRef.current || tasksList.length === 0) {
      return;
    }
    
    // Comparer avec l'état précédent pour détecter les changements
    tasksList.forEach((task) => {
      // Calculer si la carte est terminée
      let allSubTasksCompleted = false;
      if (task.subTasks && task.subTasks.length > 0) {
        const completedCount = task.subTasks.filter(st => st.status === 'Terminée').length;
        const totalCount = task.subTasks.length;
        allSubTasksCompleted = totalCount > 0 && completedCount === totalCount;
      }
      
      const isTaskCompleted = task.status === 'Terminée' && (!task.subTasks || task.subTasks.length === 0 || allSubTasksCompleted);
      
      // Identifier la tâche de manière unique
      const taskId = task.uid || task.id || `${task.title}_${task.createdAt?.toMillis()}`;
      
      // Récupérer l'état précédent (si la tâche n'existait pas avant, considérer comme non terminée)
      const wasCompleted = previousTasksStateRef.current.has(taskId) 
        ? previousTasksStateRef.current.get(taskId) || false
        : false;
      
      // Si la tâche vient de passer de "non terminée" à "terminée"
      if (isTaskCompleted && !wasCompleted) {
        // Afficher le GIF et jouer le son
        setShowCelebrationGif(true);
        
        // Jouer le son
        if (celebrationSoundRef.current) {
          celebrationSoundRef.current.currentTime = 0; // Rejouer depuis le début
          celebrationSoundRef.current.play().catch((error) => {
            console.error("Erreur lors de la lecture du son:", error);
          });
        }
        
        // Masquer le GIF après 3 secondes
        setTimeout(() => {
          setShowCelebrationGif(false);
        }, 3000);
      }
      
      // Mettre à jour l'état précédent avec l'état actuel
      previousTasksStateRef.current.set(taskId, isTaskCompleted);
    });
  }, [tasksList]);

  // Filtrer les tâches par recherche (pour l'instant pas de recherche)
  const filteredTasks = useMemo(() => {
    return tasksList;
  }, [tasksList]);

  // Séparer les listes par défaut des autres listes
  const defaultListIds = ['today', 'this-week', 'later'];
  const defaultLists = useMemo(() => {
    return lists.filter(list => defaultListIds.includes(list.id));
  }, [lists]);
  
  const customLists = useMemo(() => {
    return lists.filter(list => !defaultListIds.includes(list.id));
  }, [lists]);

  // Grouper les tâches par liste
  const tasksByList = useMemo(() => {
    const grouped: { [key: string]: Task[] } = {};
    
    // D'abord, grouper les tâches par liste
    lists.forEach(list => {
      const tasksInList = filteredTasks.filter(task => {
        // Si la tâche a un listId, l'utiliser
        if (task.listId) {
          return task.listId === list.id;
        }
        // Pour les tâches sans listId, les assigner par défaut à la première liste
        // ou utiliser un mapping basé sur le statut pour les listes par défaut
        if (list.id === 'today') {
          // Par défaut, les nouvelles tâches vont dans "Aujourd'hui"
          return !task.listId || task.status === 'En attente';
        }
        if (list.id === 'this-week') {
          return task.status === 'En cours';
        }
        if (list.id === 'later') {
          return task.status === 'Terminée';
        }
        return false;
      });
      
      // Trier les tâches de cette liste par order (0, 1, 2, 3...)
      tasksInList.sort((a, b) => {
        const orderA = a.order !== undefined && a.order !== null ? a.order : 999999;
        const orderB = b.order !== undefined && b.order !== null ? b.order : 999999;
        
        // Trier par order croissant
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        
        // Si les ordres sont égaux, utiliser createdAt comme critère de tri secondaire
        if (a.createdAt && b.createdAt) {
          return a.createdAt.toMillis() - b.createdAt.toMillis();
        }
        
        return 0;
      });
      
      grouped[list.id] = tasksInList;
    });
    
    return grouped;
  }, [filteredTasks, lists]);

  // Filtrer les tâches terminées et créer une liste avec les tâches principales et sous-tâches terminées
  const completedTasks = useMemo(() => {
    const completed: Array<{
      id: string;
      title: string;
      assignedTo?: string;
      completedAt?: Timestamp;
      isSubTask: boolean;
      parentTaskTitle?: string;
      task?: Task; // Référence à la tâche originale pour handleViewTaskInfo
    }> = [];
    
    filteredTasks.forEach(task => {
      // Tâche principale terminée
      if (task.status === 'Terminée') {
        completed.push({
          id: task.uid || task.id,
          title: task.title,
          assignedTo: task.assignedTo,
          completedAt: task.completedAt,
          isSubTask: false,
          task: task
        });
      }
      
      // Ajouter les sous-tâches terminées
      if (task.subTasks && task.subTasks.length > 0) {
        task.subTasks.forEach(subTask => {
          if (subTask.status === 'Terminée' || subTask.completed) {
            completed.push({
              id: `${task.uid || task.id}-${subTask.id}`,
              title: subTask.title,
              assignedTo: subTask.assignedTo || task.assignedTo, // Utiliser l'assigné de la sous-tâche ou de la tâche principale
              completedAt: subTask.completedAt,
              isSubTask: true,
              parentTaskTitle: task.title,
              task: task
            });
          }
        });
      }
    });
    
    // Trier par date de complétion (plus récent en premier)
    return completed.sort((a, b) => {
      if (!a.completedAt && !b.completedAt) return 0;
      if (!a.completedAt) return 1;
      if (!b.completedAt) return -1;
      return b.completedAt.toMillis() - a.completedAt.toMillis();
    });
  }, [filteredTasks]);

  // Gestion des tâches
  const handleAddTask = (listId?: string) => {
    setTaskToEdit(null);
    if (listId) {
      // Créer une tâche pré-assignée à une liste
      const newTask: Partial<Task> = { listId };
      setTaskToEdit(newTask as Task);
    }
    setShowModal(true);
  };

  const handleEditTask = (task: Task) => {
    setTaskToEdit(task);
    setShowModal(true);
  };

  const handleViewTaskInfo = (task: Task) => {
    setTaskToView(task);
    setShowInfoModal(true);
  };

  const toggleTaskDescription = (taskId: string) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const handleDeleteTask = (task: Task) => {
    setTaskToDelete(task);
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    try {
      const tasksCollection = collection(db, "Taches");
      const tasksSnapshot = await getDocs(tasksCollection);
      const taskDoc = tasksSnapshot.docs.find((d: QueryDocumentSnapshot) => {
        const data = d.data();
        return (taskToDelete.uid && (data.uid === taskToDelete.uid || d.id === taskToDelete.uid)) || 
               (data.id === taskToDelete.id && data.createdAt?.toMillis() === taskToDelete.createdAt?.toMillis());
      });
      
      if (taskDoc) {
        await deleteDoc(doc(db, "Taches", taskDoc.id));
        
        const currentUser = auth.currentUser;
        if (currentUser?.email) {
          await logUserAction(
            currentUser.email,
            "Suppression tâche",
            `Tâche supprimée: ${taskToDelete.title}`
          );
        }
        
        toast.success("Tâche supprimée avec succès");
      } else {
        toast.error("Tâche non trouvée");
      }
    } catch (error) {
      console.error("Erreur lors de la suppression de la tâche:", error);
      toast.error("Erreur lors de la suppression de la tâche");
    } finally {
      setTaskToDelete(null);
    }
  };

  const cancelDeleteTask = () => {
    setTaskToDelete(null);
  };

  // Drag and Drop
  const handleDragStart = (e: React.DragEvent, task: Task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, listId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    
    if (draggedSubTask) {
      // Si on drag une sous-tâche, mettre la liste en horizontal si elle était verticale
      // pour permettre de voir les cartes et choisir où déposer
      const sourceListId = draggedSubTask.task.listId;
      
      // Si on survole une liste différente de la source
      if (sourceListId && sourceListId !== listId) {
        // Mettre la liste cible en mode horizontal si elle était verticale
        if (verticalLists.has(listId)) {
          setTemporarilyHorizontalList(listId);
        }
        // S'assurer que la liste source n'est plus temporairement horizontale
        if (temporarilyHorizontalList === sourceListId) {
          setTemporarilyHorizontalList(null);
          // Puis mettre la liste cible
          if (verticalLists.has(listId)) {
            setTemporarilyHorizontalList(listId);
          }
        }
      } else if (sourceListId && sourceListId === listId) {
        // Même liste : garder la liste horizontale si elle était temporairement horizontale
        if (verticalLists.has(listId)) {
          setTemporarilyHorizontalList(listId);
        }
      }
    } else {
      // Si on drag une carte complète, définir draggedOverList
      setDraggedOverList(listId);
      // Si la liste est en mode vertical, la rendre temporairement horizontale
      if (verticalLists.has(listId)) {
        setTemporarilyHorizontalList(listId);
      }
    }
  };

  const handleDragOverTask = (e: React.DragEvent, taskId: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDraggedOverTask(taskId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Ne réinitialiser que si on quitte vraiment la zone de la liste (pas un sous-élément)
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentTarget = e.currentTarget as HTMLElement;
    
    // Vérifier si on quitte vraiment la zone de la liste
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      setDraggedOverTask(null);
      // Si on drag une sous-tâche, toujours réinitialiser draggedOverList pour éviter le bug "Déposer ici"
      // car draggedOverList ne devrait pas être défini lors du drag d'une sous-tâche
      if (draggedSubTask) {
        setDraggedOverList(null);
      }
      // Ne pas réinitialiser draggedOverList si on drag une carte complète, car handleDragOver le gère
      // Ne pas réinitialiser temporarilyHorizontalList ici
      // pour permettre à la liste de rester horizontale pendant le drag
      // La réinitialisation se fera dans handleDrop ou handleSubTaskDropOnTask
    }
  };

  const handleDrop = async (e: React.DragEvent, targetListId: string, targetTaskId?: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedTask) return;

    try {
      const tasksCollection = collection(db, "Taches");
      const tasksSnapshot = await getDocs(tasksCollection);
      const taskDoc = tasksSnapshot.docs.find((d: QueryDocumentSnapshot) => {
        const data = d.data();
        return (draggedTask.uid && (data.uid === draggedTask.uid || d.id === draggedTask.uid)) || 
               (data.id === draggedTask.id && data.createdAt?.toMillis() === draggedTask.createdAt?.toMillis());
      });
      
      if (!taskDoc) {
        toast.error("Tâche non trouvée");
        return;
      }

      const isSameList = draggedTask.listId === targetListId;
      
      // Si c'est la même liste, on fait un réordonnancement
      if (isSameList) {
        // Récupérer toutes les tâches de cette liste depuis Firestore (pas depuis le state local)
        const allTasksInListFromFirestore: Task[] = [];
        tasksSnapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.listId === targetListId) {
            // Initialiser l'ordre si manquant en utilisant l'index dans la liste
            let taskOrder = data.order;
            if (taskOrder === undefined || taskOrder === null) {
              // Si pas d'ordre, utiliser un ordre temporaire basé sur la position dans la liste
              taskOrder = allTasksInListFromFirestore.length;
            }
            allTasksInListFromFirestore.push({
              ...data,
              uid: data.uid || docSnap.id,
              order: taskOrder
            } as Task);
          }
        });
        
        // Trier par ordre actuel
        allTasksInListFromFirestore.sort((a, b) => {
          const orderA = a.order !== undefined && a.order !== null ? a.order : 999999;
          const orderB = b.order !== undefined && b.order !== null ? b.order : 999999;
          if (orderA === orderB && a.createdAt && b.createdAt) {
            return a.createdAt.toMillis() - b.createdAt.toMillis();
          }
          return orderA - orderB;
        });
        
        // Si certaines tâches n'ont toujours pas d'ordre, les initialiser maintenant
        let maxOrder = -1;
        allTasksInListFromFirestore.forEach(t => {
          if (t.order !== undefined && t.order !== null && t.order > maxOrder) {
            maxOrder = t.order;
          }
        });
        
        // Initialiser les ordres manquants
        for (let i = 0; i < allTasksInListFromFirestore.length; i++) {
          const task = allTasksInListFromFirestore[i];
          if (task.order === undefined || task.order === null || task.order === 999999) {
            task.order = maxOrder + 1 + i;
          }
        }
        
        // Re-trier après initialisation
        allTasksInListFromFirestore.sort((a, b) => {
          const orderA = a.order!;
          const orderB = b.order!;
          if (orderA === orderB && a.createdAt && b.createdAt) {
            return a.createdAt.toMillis() - b.createdAt.toMillis();
          }
          return orderA - orderB;
        });
        
        // Rechercher la tâche déplacée avec plusieurs méthodes
        const draggedTaskInList = allTasksInListFromFirestore.find(t => {
          // Méthode 1 : par uid
          if (draggedTask.uid && (t.uid === draggedTask.uid || t.id === draggedTask.uid)) {
            return true;
          }
          // Méthode 2 : par id
          if (draggedTask.id && t.id === draggedTask.id) {
            return true;
          }
          // Méthode 3 : par id et createdAt
          if (draggedTask.id && t.id === draggedTask.id && 
              draggedTask.createdAt && t.createdAt &&
              draggedTask.createdAt.toMillis() === t.createdAt.toMillis()) {
            return true;
          }
          return false;
        });
        
        if (!draggedTaskInList) {
          console.error("Tâche déplacée non trouvée:", {
            draggedTask: { uid: draggedTask.uid, id: draggedTask.id, title: draggedTask.title },
            allTasks: allTasksInListFromFirestore.map(t => ({ uid: t.uid, id: t.id, title: t.title }))
          });
          toast.error("Tâche non trouvée dans la liste");
          return;
        }
        
        const draggedIndex = allTasksInListFromFirestore.findIndex(t => {
          if (draggedTask.uid && (t.uid === draggedTask.uid || t.id === draggedTask.uid)) {
            return true;
          }
          if (draggedTask.id && t.id === draggedTask.id) {
            return true;
          }
          return false;
        });
        
        let newIndex: number;
        
        // Si on a un targetTaskId et que ce n'est pas la même carte
        if (targetTaskId && targetTaskId !== draggedTask.uid && targetTaskId !== draggedTask.id) {
          const targetIndex = allTasksInListFromFirestore.findIndex(t => {
            return (t.uid === targetTaskId || t.id === targetTaskId);
          });
          
          if (targetIndex === -1) {
            console.error("Carte cible non trouvée:", {
              targetTaskId,
              allTasks: allTasksInListFromFirestore.map(t => ({ uid: t.uid, id: t.id, title: t.title }))
            });
            toast.error("Carte cible non trouvée");
            return;
          }
          
          // Déterminer si on insère avant ou après la cible
          const mouseY = e.clientY;
          const mouseX = e.clientX;
          const elementAtPoint = document.elementFromPoint(mouseX, mouseY);
          
          let insertBefore = false;
          if (elementAtPoint) {
            let current: HTMLElement | null = elementAtPoint as HTMLElement;
            while (current && !current.classList.contains('taskboard-card')) {
              current = current.parentElement;
            }
            if (current) {
              const rect = current.getBoundingClientRect();
              const cardCenterY = rect.top + rect.height / 2;
              insertBefore = mouseY < cardCenterY;
            }
          }
          
          // Calculer la nouvelle position AVANT de retirer la carte
          // La logique : on calcule où insérer dans la liste complète, puis on ajuste après retrait
          if (insertBefore) {
            // Insérer avant la cible
            newIndex = targetIndex;
          } else {
            // Insérer après la cible
            newIndex = targetIndex + 1;
          }
          
          console.log("Calcul newIndex:", { 
            draggedIndex, 
            targetIndex, 
            insertBefore, 
            newIndexCalculated: newIndex 
          });
        } else {
          // Dépôt dans la zone vide : mettre à la fin
          newIndex = allTasksInListFromFirestore.length;
        }
        
        // Valider newIndex
        if (newIndex < 0) newIndex = 0;
        if (newIndex > allTasksInListFromFirestore.length) {
          newIndex = allTasksInListFromFirestore.length;
        }
        
        // Reconstruire l'ordre : retirer la carte déplacée et la réinsérer à la nouvelle position
        const updatedTasks = [...allTasksInListFromFirestore];
        
        // Retirer la carte déplacée de sa position actuelle
        updatedTasks.splice(draggedIndex, 1);
        
        // Ajuster newIndex si on a retiré un élément avant la position d'insertion
        // Si draggedIndex < newIndex, cela signifie qu'on a retiré un élément avant la position d'insertion
        // donc on doit décrémenter newIndex de 1
        let finalNewIndex = newIndex;
        if (draggedIndex < newIndex) {
          finalNewIndex = newIndex - 1;
        }
        // Si draggedIndex >= newIndex, pas besoin d'ajuster car on insère avant la position retirée
        
        // Valider finalNewIndex
        if (finalNewIndex < 0) finalNewIndex = 0;
        if (finalNewIndex > updatedTasks.length) {
          finalNewIndex = updatedTasks.length;
        }
        
        console.log("Position finale après retrait:", { 
          draggedIndex, 
          newIndex, 
          finalNewIndex,
          updatedTasksLength: updatedTasks.length
        });
        
        // Insérer à la nouvelle position
        updatedTasks.splice(finalNewIndex, 0, draggedTaskInList);
        
        console.log("Tâches après réordonnancement:", updatedTasks.map((t, i) => ({ 
          index: i,
          title: t.title, 
          uid: t.uid, 
          id: t.id 
        })));
        
        // Mettre à jour tous les ordres de manière séquentielle
        console.log("=== DÉBUT RÉORDONNANCEMENT ===");
        console.log("Tâches avant réordonnancement:", allTasksInListFromFirestore.map((t, i) => ({ 
          index: i,
          title: t.title, 
          uid: t.uid, 
          id: t.id, 
          order: t.order
        })));
        console.log("Carte déplacée:", { title: draggedTask.title, uid: draggedTask.uid, id: draggedTask.id, draggedIndex });
        if (targetTaskId) {
          const targetIndexBefore = allTasksInListFromFirestore.findIndex(t => (t.uid || t.id) === targetTaskId);
          console.log("Carte cible:", { targetTaskId, targetIndexBefore });
        }
        console.log("Nouvelle position calculée (avant retrait):", newIndex);
        
        const updatePromises: Promise<void>[] = [];
        
        for (let index = 0; index < updatedTasks.length; index++) {
          const task = updatedTasks[index];
          
          // Essayer plusieurs méthodes pour trouver le document
          let taskDocToUpdate = tasksSnapshot.docs.find((d: QueryDocumentSnapshot) => {
            const data = d.data();
            // Méthode 1 : par uid (exact match)
            if (task.uid && data.uid === task.uid) {
              return true;
            }
            // Méthode 2 : par document ID si c'est l'uid
            if (task.uid && d.id === task.uid) {
              return true;
            }
            // Méthode 3 : par id et createdAt
            if (task.id && data.id === task.id) {
              if (task.createdAt && data.createdAt) {
                return task.createdAt.toMillis() === data.createdAt.toMillis();
              }
              // Si pas de createdAt, accepter si l'id correspond
              return true;
            }
            // Méthode 4 : par document ID si c'est l'id
            if (task.id && d.id === task.id) {
              return true;
            }
            return false;
          });
          
          if (taskDocToUpdate) {
            const taskRef = doc(db, "Taches", taskDocToUpdate.id);
            console.log(`Mise à jour: "${task.title}" → order=${index} (docId: ${taskDocToUpdate.id}, uid: ${task.uid}, id: ${task.id})`);
            updatePromises.push(
              updateDoc(taskRef, {
                order: index,
                updatedAt: Timestamp.now()
              }).catch(err => {
                console.error(`Erreur lors de la mise à jour de "${task.title}":`, err);
                throw err;
              })
            );
          } else {
            console.error(`Document non trouvé pour "${task.title}" (uid: ${task.uid}, id: ${task.id})`);
            console.error("Documents disponibles:", tasksSnapshot.docs.map(d => ({
              docId: d.id,
              uid: d.data().uid,
              id: d.data().id,
              title: d.data().title
            })));
            // Ne pas bloquer, continuer avec les autres
          }
        }
        
        await Promise.all(updatePromises);
        console.log("=== FIN RÉORDONNANCEMENT ===");
        
        const currentUser = auth.currentUser;
        if (currentUser?.email) {
          await logUserAction(
            currentUser.email,
            "Réordonnancement tâche",
            `Tâche "${draggedTask.title}" réordonnée dans "${lists.find(l => l.id === targetListId)?.title}"`
          );
        }
        
        toast.success("Tâche réordonnée avec succès");
        return;
      } else {
        // Déplacement vers une autre liste ou ajout à la fin d'une liste (ou dépôt dans la zone de la liste sans cible)
        const targetListTasks = tasksList.filter(t => t.listId === targetListId);
        const maxOrder = targetListTasks.length > 0 
          ? Math.max(...targetListTasks.map(t => t.order || 0))
          : -1;
        
        const taskRef = doc(db, "Taches", taskDoc.id);
        const updateData: any = {
          listId: targetListId,
          updatedAt: Timestamp.now()
        };
        
        // Si on change de liste, réinitialiser l'ordre à la fin
        if (!isSameList) {
          updateData.order = maxOrder + 1;
        } else {
          // Si c'est la même liste mais qu'on n'a pas de targetTaskId (dépôt dans la zone vide)
          // On garde l'ordre actuel ou on le met à la fin
          if (draggedTask.order === undefined || draggedTask.order === null) {
            updateData.order = maxOrder + 1;
          } else {
            // On garde l'ordre actuel
            updateData.order = draggedTask.order;
          }
        }
        
        await updateDoc(taskRef, updateData);
        
        const currentUser = auth.currentUser;
        if (currentUser?.email) {
          await logUserAction(
            currentUser.email,
            "Déplacement tâche",
            `Tâche "${draggedTask.title}" déplacée vers "${lists.find(l => l.id === targetListId)?.title}"`
          );
        }
        
        toast.success("Tâche déplacée avec succès");
      }
    } catch (error: any) {
      console.error("Erreur lors du déplacement de la tâche:", error);
      const errorMessage = error?.message || "Erreur inconnue";
      toast.error(`Erreur lors du déplacement de la tâche: ${errorMessage}`);
    } finally {
      setDraggedTask(null);
      setDraggedOverList(null);
      setDraggedOverTask(null);
      setTemporarilyHorizontalList(null);
    }
  };

  const handleToggleTaskStatus = async (task: Task) => {
    try {
      const tasksCollection = collection(db, "Taches");
      const tasksSnapshot = await getDocs(tasksCollection);
      const taskDoc = tasksSnapshot.docs.find((d: QueryDocumentSnapshot) => {
        const data = d.data();
        return (task.uid && (data.uid === task.uid || d.id === task.uid)) || 
               (data.id === task.id && data.createdAt?.toMillis() === task.createdAt?.toMillis());
      });
      
      if (taskDoc) {
        const taskRef = doc(db, "Taches", taskDoc.id);
        const newStatus = task.status === 'Terminée' ? 'En cours' : 'Terminée';
        
        const updateData: any = {
          status: newStatus,
          updatedAt: Timestamp.now()
        };
        
        // Si la tâche passe à "Terminée", enregistrer la date de complétion
        if (newStatus === 'Terminée') {
          updateData.completedAt = Timestamp.now();
        }
        
        await updateDoc(taskRef, updateData);
        
        const currentUser = auth.currentUser;
        if (currentUser?.email) {
          await logUserAction(
            currentUser.email,
            "Changement statut tâche",
            `Tâche "${task.title}" : ${task.status} → ${newStatus}`
          );
        }
        
        toast.success(`Tâche marquée comme ${newStatus}`);
      } else {
        toast.error("Tâche non trouvée");
      }
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error);
      toast.error("Erreur lors du changement de statut");
    }
  };

  const handleToggleSubTaskStatus = async (task: Task, subTaskId: string) => {
    try {
      if (!task.subTasks) return;
      
      const tasksCollection = collection(db, "Taches");
      const tasksSnapshot = await getDocs(tasksCollection);
      const taskDoc = tasksSnapshot.docs.find((d: QueryDocumentSnapshot) => {
        const data = d.data();
        return (task.uid && (data.uid === task.uid || d.id === task.uid)) || 
               (data.id === task.id && data.createdAt?.toMillis() === task.createdAt?.toMillis());
      });
      
      if (taskDoc) {
        const taskRef = doc(db, "Taches", taskDoc.id);
        const updatedSubTasks = task.subTasks.map(st => {
          if (st.id === subTaskId) {
            const currentStatus = st.status || 'En attente';
            const newStatus = currentStatus === 'Terminée' ? 'En attente' : 'Terminée';
            const isCompleted = newStatus === 'Terminée';
            const updatedSubTask: SubTask = { 
              ...st, 
              status: newStatus, 
              completed: isCompleted // S'assurer que completed est explicitement true ou false
            };
            // Si la sous-tâche passe à "Terminée", enregistrer la date de complétion
            if (isCompleted) {
              updatedSubTask.completedAt = Timestamp.now();
            } else {
              // Si la sous-tâche passe de "Terminée" à autre chose, mettre completedAt à null pour Firestore
              updatedSubTask.completedAt = null as any; // null pour Firestore
            }
            return updatedSubTask;
          }
          // Pour les autres sous-tâches, s'assurer que completed correspond au statut
          const isTaskCompleted = st.status === 'Terminée' || st.completed === true;
          return {
            ...st,
            completed: isTaskCompleted // S'assurer que completed correspond toujours au statut
          };
        });
        
        // Utiliser UNIQUEMENT le statut pour compter les sous-tâches terminées
        const completedCount = updatedSubTasks.filter(st => st.status === 'Terminée').length;
        const totalCount = updatedSubTasks.length;
        const allSubTasksCompleted = totalCount > 0 && completedCount === totalCount;
        
        // Nettoyer les sous-tâches pour Firestore : s'assurer que completed correspond toujours au statut
        const cleanedSubTasks = updatedSubTasks.map(st => {
          // Déterminer si la tâche est terminée UNIQUEMENT selon le statut
          const isTaskCompleted = st.status === 'Terminée';
          return {
            id: st.id,
            title: st.title,
            completed: isTaskCompleted, // completed = true SEULEMENT si status === 'Terminée'
            assignedTo: st.assignedTo || null,
            status: st.status || null,
            completedAt: isTaskCompleted ? (st.completedAt || null) : null, // null si pas terminée
            order: st.order !== undefined && st.order !== null ? st.order : null
          };
        });
        
        // Si toutes les sous-tâches sont terminées, mettre à jour le statut de la tâche principale
        const updateData: any = {
          subTasks: cleanedSubTasks,
          completedItems: completedCount,
          totalItems: totalCount, // Mettre à jour totalItems aussi
          updatedAt: Timestamp.now()
        };
        
        // Si toutes les sous-tâches sont terminées et que la tâche principale n'est pas déjà "Terminée", la marquer comme terminée
        if (allSubTasksCompleted && task.status !== 'Terminée') {
          updateData.status = 'Terminée';
          updateData.completedAt = Timestamp.now();
        } else if (!allSubTasksCompleted && task.status === 'Terminée') {
          // Si toutes les sous-tâches ne sont plus terminées et que la tâche principale est "Terminée", la remettre en "En cours" ou "En attente"
          // On garde le statut "Terminée" seulement si toutes les sous-tâches sont terminées
          // Sinon, on remet le statut précédent ou "En cours"
          updateData.status = 'En cours';
        }
        
        await updateDoc(taskRef, updateData);
        
        const currentUser = auth.currentUser;
        if (currentUser?.email) {
          const subTask = task.subTasks.find(st => st.id === subTaskId);
          await logUserAction(
            currentUser.email,
            "Changement statut sous-tâche",
            `Sous-tâche "${subTask?.title}" : ${subTask?.status} → ${subTask?.status === 'Terminée' ? 'En attente' : 'Terminée'}`
          );
        }
      } else {
        toast.error("Tâche non trouvée");
      }
    } catch (error) {
      console.error("Erreur lors du changement de statut de la sous-tâche:", error);
      toast.error("Erreur lors du changement de statut");
    }
  };

  // Drag and Drop pour les sous-tâches
  const handleSubTaskDragStart = (e: React.DragEvent, task: Task, subTask: SubTask) => {
    // Réinitialiser le flag immédiatement
    successfulDropRef.current = false;
    
    // Réinitialiser tous les états de drag pour éviter les conflits
    setDraggedOverSubTask(null);
    setDraggedOverTaskForSubTask(null);
    setDraggedOverList(null);
    setDraggedOverTask(null);
    
    // Définir directement la nouvelle sous-tâche en cours de drag
    // Ne pas vérifier si draggedSubTask existe, car il devrait être null après le drop
    setDraggedSubTask({ task, subTask });
    e.dataTransfer.effectAllowed = "move";
    
    // Mettre la liste source en mode horizontal temporairement si elle était verticale
    if (task.listId && verticalLists.has(task.listId)) {
      setTemporarilyHorizontalList(task.listId);
    }
  };

  const handleSubTaskDragOver = (e: React.DragEvent, subTaskId: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDraggedOverSubTask(subTaskId);
  };

  const handleSubTaskDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDraggedOverSubTask(null);
    }
  };

  const handleSubTaskDropOnTask = async (e: React.DragEvent, targetTask: Task) => {
    e.preventDefault();
    e.stopPropagation();
    
    // CAPTURER draggedSubTask immédiatement pour éviter qu'il soit null pendant le traitement asynchrone
    const currentDraggedSubTask = draggedSubTask;
    
    // Vérifier si draggedSubTask existe, sinon ne rien faire
    if (!currentDraggedSubTask) {
      // Nettoyer les états si draggedSubTask n'existe pas
      setDraggedOverSubTask(null);
      setDraggedOverTaskForSubTask(null);
      setDraggedOverList(null);
      setDraggedOverTask(null);
      return;
    }

    // Si c'est la même carte, ne rien faire (utiliser handleSubTaskDrop pour réordonner)
    if (currentDraggedSubTask.task.uid === targetTask.uid || currentDraggedSubTask.task.id === targetTask.id) {
      // Nettoyer les états mais garder draggedSubTask pour permettre un nouveau drag
      setDraggedOverSubTask(null);
      setDraggedOverTaskForSubTask(null);
      setDraggedOverList(null);
      setDraggedOverTask(null);
      return;
    }

    try {
      const tasksCollection = collection(db, "Taches");
      const tasksSnapshot = await getDocs(tasksCollection);
      
      // Trouver la tâche source (utiliser currentDraggedSubTask capturé au début)
      const sourceTaskDoc = tasksSnapshot.docs.find((d: QueryDocumentSnapshot) => {
        const data = d.data();
        return (currentDraggedSubTask.task.uid && (data.uid === currentDraggedSubTask.task.uid || d.id === currentDraggedSubTask.task.uid)) || 
               (currentDraggedSubTask.task.id && data.id === currentDraggedSubTask.task.id && 
                currentDraggedSubTask.task.createdAt && data.createdAt?.toMillis() === currentDraggedSubTask.task.createdAt?.toMillis());
      });

      // Trouver la tâche cible
      const targetTaskDoc = tasksSnapshot.docs.find((d: QueryDocumentSnapshot) => {
        const data = d.data();
        return (targetTask.uid && (data.uid === targetTask.uid || d.id === targetTask.uid)) || 
               (targetTask.id && data.id === targetTask.id && 
                targetTask.createdAt && data.createdAt?.toMillis() === targetTask.createdAt?.toMillis());
      });

      if (!sourceTaskDoc || !targetTaskDoc) {
        toast.error("Tâche non trouvée");
        return;
      }

      // Retirer la sous-tâche de la carte source (utiliser currentDraggedSubTask)
      const sourceSubTasks = (currentDraggedSubTask.task.subTasks || []).filter(st => st.id !== currentDraggedSubTask.subTask.id);
      
      // Calculer les completedItems et totalItems pour la carte source
      const sourceCompletedCount = sourceSubTasks.filter(st => st.status === 'Terminée' || st.completed).length;
      const sourceTotalCount = sourceSubTasks.length;

      // Ajouter la sous-tâche à la carte cible
      const targetSubTasks = targetTask.subTasks || [];
      const maxOrder = targetSubTasks.length > 0 
        ? Math.max(...targetSubTasks.map(st => st.order !== undefined && st.order !== null ? st.order : -1))
        : -1;
      
      const movedSubTask = {
        ...currentDraggedSubTask.subTask,
        order: maxOrder + 1
      };
      
      const updatedTargetSubTasks = [...targetSubTasks, movedSubTask];
      
      // Calculer les completedItems et totalItems pour la carte cible
      const targetCompletedCount = updatedTargetSubTasks.filter(st => st.status === 'Terminée' || st.completed).length;
      const targetTotalCount = updatedTargetSubTasks.length;

      // Mettre à jour les deux cartes
      const sourceTaskRef = doc(db, "Taches", sourceTaskDoc.id);
      const targetTaskRef = doc(db, "Taches", targetTaskDoc.id);

      await Promise.all([
        updateDoc(sourceTaskRef, {
          subTasks: sourceSubTasks,
          completedItems: sourceCompletedCount,
          totalItems: sourceTotalCount,
          updatedAt: Timestamp.now()
        }),
        updateDoc(targetTaskRef, {
          subTasks: updatedTargetSubTasks,
          completedItems: targetCompletedCount,
          totalItems: targetTotalCount,
          updatedAt: Timestamp.now()
        })
      ]);

      // Mettre à jour taskToView si la modale est ouverte (utiliser currentDraggedSubTask)
      if (taskToView) {
        if (taskToView.uid === currentDraggedSubTask.task.uid || taskToView.id === currentDraggedSubTask.task.id) {
          setTaskToView({
            ...taskToView,
            subTasks: sourceSubTasks,
            completedItems: sourceCompletedCount,
            totalItems: sourceTotalCount
          });
        } else if (taskToView.uid === targetTask.uid || taskToView.id === targetTask.id) {
          setTaskToView({
            ...taskToView,
            subTasks: updatedTargetSubTasks,
            completedItems: targetCompletedCount,
            totalItems: targetTotalCount
          });
        }
      }

      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Déplacement sous-tâche",
          `Sous-tâche "${currentDraggedSubTask.subTask.title}" déplacée de "${currentDraggedSubTask.task.title}" vers "${targetTask.title}"`
        );
      }

      // Marquer qu'un drop réussi a eu lieu
      successfulDropRef.current = true;
      
      // Gérer les listes : garder la liste de destination horizontale, remettre la source en vertical
      // (utiliser currentDraggedSubTask)
      const sourceListId = currentDraggedSubTask.task.listId;
      const targetListId = targetTask.listId;
      
      if (sourceListId && targetListId && sourceListId !== targetListId) {
        // Listes différentes : garder UNIQUEMENT la liste de destination horizontale
        // La liste source doit être dans verticalLists pour redevenir verticale
        const targetWasVertical = verticalLists.has(targetListId);
        
        // S'assurer que la liste source est dans verticalLists pour qu'elle redevienne verticale
        // Même si c'est 'today', on l'ajoute temporairement à verticalLists
        setVerticalLists(prev => {
          const newSet = new Set(prev);
          if (sourceListId !== targetListId) {
            newSet.add(sourceListId);
          }
          return newSet;
        });
        
        // Mettre uniquement la liste de destination dans temporarilyHorizontalList
        if (targetWasVertical) {
          // La liste de destination était verticale, la mettre temporairement horizontale
          setTemporarilyHorizontalList(targetListId);
        } else {
          // Si la liste de destination n'était pas verticale, la mettre temporairement horizontale
          setTemporarilyHorizontalList(targetListId);
        }
      } else if (sourceListId && targetListId && sourceListId === targetListId) {
        // Même liste : si elle était verticale, la garder temporairement horizontale
        if (verticalLists.has(sourceListId)) {
          setTemporarilyHorizontalList(sourceListId);
        } else {
          setTemporarilyHorizontalList(null);
        }
      } else {
        // Pas de listId, réinitialiser
        setTemporarilyHorizontalList(null);
      }

      toast.success("Sous-tâche déplacée avec succès");
    } catch (error: any) {
      console.error("Erreur lors du déplacement de la sous-tâche:", error);
      toast.error(`Erreur lors du déplacement: ${error?.message || 'Erreur inconnue'}`);
    } finally {
      // Réinitialiser tous les états de drag pour permettre un nouveau drag
      // Utiliser un délai plus long pour s'assurer que le drop est complètement traité
      // et que tous les événements sont bien propagés
      setTimeout(() => {
        setDraggedSubTask(null);
        setDraggedOverSubTask(null);
        setDraggedOverTaskForSubTask(null);
        setDraggedOverList(null);
        setDraggedOverTask(null);
        successfulDropRef.current = false;
      }, 200);
      // temporarilyHorizontalList est géré dans le code avant le finally
    }
  };

  const handleSubTaskDrop = async (e: React.DragEvent, task: Task, targetSubTaskId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedSubTask) {
      setDraggedSubTask(null);
      setDraggedOverSubTask(null);
      setDraggedOverTaskForSubTask(null);
      return;
    }
    
    // Si ce n'est pas la même carte, ne rien faire (utiliser handleSubTaskDropOnTask)
    if (draggedSubTask.task.uid !== task.uid && draggedSubTask.task.id !== task.id) {
      setDraggedSubTask(null);
      setDraggedOverSubTask(null);
      setDraggedOverTaskForSubTask(null);
      return;
    }

    try {
      const tasksCollection = collection(db, "Taches");
      const tasksSnapshot = await getDocs(tasksCollection);
      const taskDoc = tasksSnapshot.docs.find((d: QueryDocumentSnapshot) => {
        const data = d.data();
        return (task.uid && (data.uid === task.uid || d.id === task.uid)) || 
               (data.id === task.id && data.createdAt?.toMillis() === task.createdAt?.toMillis());
      });
      
      if (!taskDoc) {
        toast.error("Tâche non trouvée");
        return;
      }

      const currentSubTasks = task.subTasks || [];
      
      // Initialiser l'ordre si manquant
      const subTasksWithOrder = currentSubTasks.map((st, index) => ({
        ...st,
        order: st.order !== undefined && st.order !== null ? st.order : index
      }));

      // Trier par ordre
      subTasksWithOrder.sort((a, b) => {
        const orderA = a.order !== undefined && a.order !== null ? a.order : 999999;
        const orderB = b.order !== undefined && b.order !== null ? b.order : 999999;
        return orderA - orderB;
      });

      // Trouver les indices
      const draggedIndex = subTasksWithOrder.findIndex(st => st.id === draggedSubTask.subTask.id);
      const targetIndex = subTasksWithOrder.findIndex(st => st.id === targetSubTaskId);

      if (draggedIndex === -1 || targetIndex === -1) {
        toast.error("Sous-tâche non trouvée");
        return;
      }

      // Réordonner
      const updatedSubTasks = [...subTasksWithOrder];
      const [removed] = updatedSubTasks.splice(draggedIndex, 1);
      updatedSubTasks.splice(targetIndex, 0, removed);

      // Mettre à jour les ordres
      updatedSubTasks.forEach((st, index) => {
        st.order = index;
      });

      const taskRef = doc(db, "Taches", taskDoc.id);
      await updateDoc(taskRef, {
        subTasks: updatedSubTasks,
        updatedAt: Timestamp.now()
      });

      // Mettre à jour taskToView si la modale est ouverte pour cette tâche
      if (taskToView && (taskToView.uid === task.uid || taskToView.id === task.id)) {
        setTaskToView({
          ...taskToView,
          subTasks: updatedSubTasks
        });
      }

      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Réordonnancement sous-tâche",
          `Sous-tâche "${draggedSubTask.subTask.title}" réordonnée dans "${task.title}"`
        );
      }

      // Marquer qu'un drop réussi a eu lieu
      successfulDropRef.current = true;

      toast.success("Sous-tâche réordonnée avec succès");
    } catch (error: any) {
      console.error("Erreur lors du réordonnancement de la sous-tâche:", error);
      toast.error(`Erreur lors du réordonnancement: ${error?.message || 'Erreur inconnue'}`);
    } finally {
      // Réinitialiser tous les états de drag pour permettre un nouveau drag
      // Utiliser un petit délai pour s'assurer que le drop est complètement traité
      setTimeout(() => {
        setDraggedSubTask(null);
        setDraggedOverSubTask(null);
        setDraggedOverTaskForSubTask(null);
        setDraggedOverList(null);
        setDraggedOverTask(null);
        successfulDropRef.current = false;
      }, 50);
    }
  };

  const handleAddList = async () => {
    if (!newListTitle.trim()) {
      toast.error("Le titre de la liste est requis");
      return;
    }

    try {
      const docRef = doc(db, "administration", "taskboard-lists");
      const docSnap = await getDoc(docRef);
      
      const newList: TaskBoardList = {
        id: Date.now().toString(),
        title: newListTitle,
        color: newListColor
      };
      
      // Toujours inclure les listes par défaut
      const defaultLists: TaskBoardList[] = [
        { id: 'today', title: 'Aujourd\'hui', color: 'orange' as const },
        { id: 'this-week', title: 'Cette semaine', color: 'green' as const },
        { id: 'later', title: 'Plus tard', color: 'grey' as const }
      ];
      const defaultListIds = ['today', 'this-week', 'later'];
      
      let updatedLists: TaskBoardList[];
      
      if (docSnap.exists()) {
        const existingLists = docSnap.data().lists || [];
        // Préserver les listes par défaut existantes ou utiliser celles par défaut
        const existingDefaultLists = existingLists.filter((list: TaskBoardList) => defaultListIds.includes(list.id));
        const mergedDefaultLists: TaskBoardList[] = defaultListIds.map(id => {
          const existing = existingDefaultLists.find((list: TaskBoardList) => list.id === id);
          return existing || defaultLists.find(list => list.id === id)!;
        });
        const customLists = existingLists.filter((list: TaskBoardList) => !defaultListIds.includes(list.id));
        updatedLists = [...mergedDefaultLists, ...customLists, newList];
      } else {
        // Si le document n'existe pas, créer avec les listes par défaut + la nouvelle
        updatedLists = [...defaultLists, newList];
      }
      
      // Utiliser setDoc avec merge si le document n'existe pas, sinon updateDoc
      if (docSnap.exists()) {
        await updateDoc(docRef, { lists: updatedLists });
      } else {
        await setDoc(docRef, { lists: updatedLists });
      }
      
      // Ajouter la nouvelle liste au Set des listes verticales pour garantir qu'une seule liste soit horizontale
      setVerticalLists(prev => {
        const newSet = new Set(prev);
        newSet.add(newList.id);
        return newSet;
      });
      
      setNewListTitle("");
      setShowAddListModal(false);
      toast.success("Liste ajoutée avec succès");
    } catch (error: any) {
      console.error("Erreur lors de l'ajout de la liste:", error);
      toast.error(`Erreur lors de l'ajout de la liste: ${error.message || 'Permission refusée'}`);
    }
  };

  const handleEditList = (list: TaskBoardList) => {
    setListToEdit(list);
    setEditListTitle(list.title);
    setEditListColor(list.color);
    setShowEditListModal(true);
  };

  const handleSaveList = async () => {
    if (!editListTitle.trim()) {
      toast.error("Le titre de la liste est requis");
      return;
    }

    if (!listToEdit) {
      return;
    }

    try {
      const docRef = doc(db, "administration", "taskboard-lists");
      const docSnap = await getDoc(docRef);
      
      const defaultListIds = ['today', 'this-week', 'later'];
      const defaultLists: TaskBoardList[] = [
        { id: 'today', title: 'Aujourd\'hui', color: 'orange' as const },
        { id: 'this-week', title: 'Cette semaine', color: 'green' as const },
        { id: 'later', title: 'Plus tard', color: 'grey' as const }
      ];
      
      if (docSnap.exists()) {
        const currentLists = docSnap.data().lists || [];
        
        // Vérifier si la liste existe déjà
        const listExists = currentLists.some((l: TaskBoardList) => l.id === listToEdit.id);
        
        let updatedLists: TaskBoardList[];
        
        if (listExists) {
          // Mettre à jour la liste existante
          updatedLists = currentLists.map((l: TaskBoardList) => 
            l.id === listToEdit.id 
              ? { ...l, title: editListTitle.trim(), color: editListColor }
              : l
          );
        } else {
          // Ajouter la liste si elle n'existe pas (cas des listes par défaut)
          updatedLists = [...currentLists, { ...listToEdit, title: editListTitle.trim(), color: editListColor }];
        }
        
        // S'assurer que toutes les listes par défaut sont présentes
        const existingDefaultLists = updatedLists.filter((list: TaskBoardList) => defaultListIds.includes(list.id));
        const mergedDefaultLists: TaskBoardList[] = defaultListIds.map(id => {
          const existing = existingDefaultLists.find((list: TaskBoardList) => list.id === id);
          if (id === listToEdit.id) {
            // Utiliser la valeur modifiée pour la liste en cours d'édition
            return { id, title: editListTitle.trim(), color: editListColor };
          }
          return existing || defaultLists.find(list => list.id === id)!;
        });
        const customLists = updatedLists.filter((list: TaskBoardList) => !defaultListIds.includes(list.id));
        const finalLists = [...mergedDefaultLists, ...customLists];
        
        await updateDoc(docRef, { lists: finalLists });
      } else {
        // Si le document n'existe pas, le créer avec toutes les listes par défaut et la liste modifiée
        const defaultListsWithEdit: TaskBoardList[] = defaultListIds.map(id => {
          if (id === listToEdit.id) {
            return { id, title: editListTitle.trim(), color: editListColor };
          }
          return defaultLists.find(list => list.id === id)!;
        });
        await setDoc(docRef, { lists: defaultListsWithEdit });
      }
      
      setShowEditListModal(false);
      setListToEdit(null);
      toast.success("Liste modifiée avec succès");
    } catch (error) {
      console.error("Erreur lors de la modification de la liste:", error);
      toast.error("Erreur lors de la modification de la liste");
    }
  };

  const handleDeleteList = async () => {
    if (!listToEdit) {
      return;
    }

    // Empêcher la suppression des listes par défaut
    const defaultListIds = ['today', 'this-week', 'later'];
    if (defaultListIds.includes(listToEdit.id)) {
      toast.error("Impossible de supprimer une liste par défaut");
      return;
    }

    // Demander confirmation
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la liste "${listToEdit.title}" ? Cette action est irréversible.`)) {
      return;
    }

    try {
      const docRef = doc(db, "administration", "taskboard-lists");
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentLists = docSnap.data().lists || [];
        // Filtrer pour supprimer la liste
        const updatedLists = currentLists.filter((l: TaskBoardList) => l.id !== listToEdit.id);
        
        // Toujours inclure les listes par défaut
        const defaultLists: TaskBoardList[] = [
          { id: 'today', title: 'Aujourd\'hui', color: 'orange' as const },
          { id: 'this-week', title: 'Cette semaine', color: 'green' as const },
          { id: 'later', title: 'Plus tard', color: 'grey' as const }
        ];
        const defaultListIds = ['today', 'this-week', 'later'];
        
        // Préserver les listes par défaut
        const existingDefaultLists = updatedLists.filter((list: TaskBoardList) => defaultListIds.includes(list.id));
        const mergedDefaultLists: TaskBoardList[] = defaultListIds.map(id => {
          const existing = existingDefaultLists.find((list: TaskBoardList) => list.id === id);
          return existing || defaultLists.find(list => list.id === id)!;
        });
        const customLists = updatedLists.filter((list: TaskBoardList) => !defaultListIds.includes(list.id));
        const finalLists = [...mergedDefaultLists, ...customLists];
        
        await updateDoc(docRef, { lists: finalLists });
        
        setShowEditListModal(false);
        setListToEdit(null);
        toast.success("Liste supprimée avec succès");
      }
    } catch (error) {
      console.error("Erreur lors de la suppression de la liste:", error);
      toast.error("Erreur lors de la suppression de la liste");
    }
  };

  const toggleListVertical = (listId: string) => {
    // Réinitialiser la liste temporairement horizontale lors d'un toggle manuel
    setTemporarilyHorizontalList(null);
    
    setVerticalLists(prev => {
      const newSet = new Set<string>();
      const isCurrentlyVertical = prev.has(listId);
      
      if (isCurrentlyVertical) {
        // Si la liste est verticale, on la rend horizontale
        // On met TOUTES les autres listes en vertical (garantit qu'une seule soit horizontale)
        lists.forEach(list => {
          if (list.id !== listId) {
            newSet.add(list.id);
          }
        });
      } else {
        // Si la liste est horizontale, on la rend verticale
        // On met toutes les listes en vertical sauf "today" par défaut
        lists.forEach(list => {
          if (list.id !== 'today') {
            newSet.add(list.id);
          }
        });
      }
      return newSet;
    });
  };

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  return (
    <div className="taskboard-container" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <ToastContainer position="top-right" autoClose={2000} />
      <Header 
        isAuthenticated={true} 
        onLoginClick={() => navigate('/login')} 
        onHomeClick={() => navigate('/')} 
        onLogoutClick={handleLogout}
      />
      
      <div className="taskboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Planner</h2>
        <button 
          className="taskboard-add-list-button"
          onClick={() => setShowAddListModal(true)}
        >
          <i className="bi bi-plus"></i>
          Ajouter une autre liste
        </button>
      </div>

      {/* Container principal avec listes et tâches terminées */}
      <div className="taskboard" style={{ gap: '1.5rem' }}>
        {[...defaultLists, ...customLists].map((list) => {
          const tasks = tasksByList[list.id] || [];
          // Si la liste est temporairement en mode horizontal pendant le drag, l'afficher en horizontal
          const isVertical = temporarilyHorizontalList === list.id ? false : verticalLists.has(list.id);
          
          if (isVertical) {
            // Mode vertical - liste minimisée (sans container)
            return (
              <div 
                key={list.id}
                style={{
                  width: '55px',
                  minWidth: '55px',
                  height: 'auto',
                  minHeight: '250px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '8px 1px'
                }}
                onDragOver={(e) => handleDragOver(e, list.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, list.id)}
              >
                <div 
                  style={{
                    backgroundColor: list.color === 'orange' ? '#d97706' : 
                                    list.color === 'green' ? '#059669' : 
                                    list.color === 'blue' ? '#2563eb' : 
                                    list.color === 'purple' ? '#7c3aed' : '#4b5563',
                    borderRadius: '8px',
                    padding: '8px 6px',
                    width: '100%',
                    minHeight: '280px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleListVertical(list.id)}
                >
                  <i 
                    className="bi bi-arrows"
                    style={{ 
                      color: 'white', 
                      fontSize: '14px',
                      marginBottom: '4px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleListVertical(list.id);
                    }}
                  ></i>
                  <div style={{
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    transform: 'rotate(180deg)',
                    color: '#e5e7eb',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    marginTop: 'auto',
                    marginBottom: '12px',
                    fontFamily: "'Poppins', sans-serif"
                  }}>
                    {list.title}
                  </div>
                </div>
              </div>
            );
          }
          
          return (
            <div key={list.id} className="taskboard-list">
              <div className={`taskboard-list-header ${list.color}`}>
                <span>{list.title}</span>
                <div className="taskboard-list-header-icons">
                  <i 
                    className="bi bi-arrows-vertical"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleListVertical(list.id);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Minimiser la liste"
                  ></i>
                  <i 
                    className="bi bi-three-dots" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditList(list);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Modifier la liste"
                  ></i>
                </div>
              </div>
              
              <div 
                className="taskboard-list-content"
                onDragOver={(e) => handleDragOver(e, list.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, list.id)}
              >
                {tasks.map((task, index) => {
                  // Déterminer si la tâche est terminée (statut terminée ou toutes les sous-tâches terminées à 100%)
                  // Calculer directement à partir des sous-tâches pour plus de fiabilité
                  let allSubTasksCompleted = false;
                  if (task.subTasks && task.subTasks.length > 0) {
                    const completedCount = task.subTasks.filter(st => st.status === 'Terminée').length;
                    const totalCount = task.subTasks.length;
                    allSubTasksCompleted = totalCount > 0 && completedCount === totalCount;
                  }
                  
                  // La carte est terminée si :
                  // 1. Le statut est "Terminée" ET (pas de sous-tâches OU toutes les sous-tâches sont terminées)
                  // Si une sous-tâche n'est plus terminée, la carte ne doit plus être verte même si le statut est "Terminée"
                  const isTaskCompleted = task.status === 'Terminée' && (!task.subTasks || task.subTasks.length === 0 || allSubTasksCompleted);
                  
                  // Background color : vert (#2ecc71) si terminée, gris foncé (#34495e) sinon
                  const cardBackgroundColor = isTaskCompleted ? '#2ecc71' : '#34495e';
                  
                  // Couleurs pour les éléments quand la carte est terminée (background vert)
                  const textColor = isTaskCompleted ? '#ffffff' : '#ffffff';
                  const iconColor = isTaskCompleted ? '#ffffff' : '#ffffff';
                  const checkboxColor = isTaskCompleted ? '#ffffff' : '#9ca3af';
                  const checkboxCheckedColor = isTaskCompleted ? '#155724' : '#10b981'; // Vert foncé sur fond vert clair
                  const descriptionColor = isTaskCompleted ? 'rgba(255, 255, 255, 0.95)' : '#d1d5db';
                  const metaTextColor = isTaskCompleted ? 'rgba(255, 255, 255, 0.9)' : '#9ca3af';
                  
                  return (
                  <div
                    key={task.uid || (task.id + '-' + index)}
                    className={`taskboard-card ${draggedTask?.uid === task.uid || draggedTask?.id === task.id ? 'dragging' : ''} ${draggedOverTask === (task.uid || task.id) ? 'drag-over' : ''} ${task.completed ? 'taskboard-card-completed' : ''}`}
                    style={{ 
                      backgroundColor: cardBackgroundColor,
                      border: draggedSubTask && draggedOverTaskForSubTask === (task.uid || task.id) && draggedSubTask.task.uid !== task.uid && draggedSubTask.task.id !== task.id 
                        ? '3px dashed #007bff' 
                        : 'none',
                      boxShadow: draggedSubTask && draggedOverTaskForSubTask === (task.uid || task.id) && draggedSubTask.task.uid !== task.uid && draggedSubTask.task.id !== task.id
                        ? '0 4px 12px rgba(0, 123, 255, 0.3)'
                        : undefined
                    }}
                    draggable
                    onDragStart={(e) => {
                      // Ne pas permettre le drag de la carte si on drag une sous-tâche
                      const target = e.target as HTMLElement;
                      // Vérifier si le drag vient d'une sous-tâche (élément avec grip-vertical ou dans un conteneur de sous-tâche)
                      if (target.closest('.subtask-item') || target.classList.contains('bi-grip-vertical') || target.closest('[data-subtask]')) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      handleDragStart(e, task);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Si on drag une sous-tâche, gérer le survol de la carte
                      if (draggedSubTask) {
                        setDraggedOverTaskForSubTask(task.uid || task.id);
                        const sourceListId = draggedSubTask.task.listId;
                        const targetListId = task.listId;
                        const taskId = task.uid || task.id;
                        
                        // Expander automatiquement la carte pour afficher ses détails et sous-tâches
                        if (!expandedTasks.has(taskId)) {
                          setExpandedTasks(prev => {
                            const newSet = new Set(prev);
                            newSet.add(taskId);
                            return newSet;
                          });
                        }
                        
                        // Si on survole une carte d'une autre liste
                        if (sourceListId && targetListId && sourceListId !== targetListId) {
                          // Mettre uniquement la liste cible en mode horizontal si elle était verticale
                          // La liste source revient automatiquement en vertical (pas dans temporarilyHorizontalList)
                          if (verticalLists.has(targetListId)) {
                            setTemporarilyHorizontalList(targetListId);
                          } else {
                            // Si la liste cible n'était pas verticale, réinitialiser
                            setTemporarilyHorizontalList(null);
                          }
                        } else if (sourceListId && targetListId && sourceListId === targetListId) {
                          // Même liste : garder la liste horizontale si elle était temporairement horizontale
                          if (verticalLists.has(sourceListId)) {
                            setTemporarilyHorizontalList(sourceListId);
                          }
                        }
                      } else {
                        handleDragOverTask(e, task.uid || task.id);
                      }
                    }}
                    onDragLeave={(e) => {
                      e.stopPropagation();
                      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        if (draggedSubTask) {
                          setDraggedOverTaskForSubTask(null);
                          // Si on quitte la carte cible, remettre sa liste en vertical si elle était verticale
                          if (task.listId && verticalLists.has(task.listId)) {
                            // Ne pas réinitialiser immédiatement, attendre un peu
                            setTimeout(() => {
                              if (!draggedOverTaskForSubTask || draggedOverTaskForSubTask !== (task.uid || task.id)) {
                                if (temporarilyHorizontalList === task.listId) {
                                  setTemporarilyHorizontalList(null);
                                }
                              }
                            }, 100);
                          }
                        } else {
                          setDraggedOverTask(null);
                        }
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Si on drag une sous-tâche, déplacer vers cette carte
                      // Vérifier draggedSubTask avant d'appeler handleSubTaskDropOnTask
                      if (draggedSubTask) {
                        // S'assurer que draggedSubTask existe avant de faire le drop
                        handleSubTaskDropOnTask(e, task);
                      } else {
                        // Si pas de sous-tâche en drag, gérer le drop de carte normale
                        handleDrop(e, list.id, task.uid || task.id);
                      }
                    }}
                  >
                    <div className="taskboard-card-header">
                      <div 
                        className="taskboard-card-title" 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTaskDescription(task.uid || task.id);
                        }}
                        style={{ 
                          cursor: 'pointer',
                          textDecoration: task.subTaskMode !== "Collectif" && task.status === 'Terminée' ? 'line-through' : 'none',
                          opacity: task.subTaskMode !== "Collectif" && task.status === 'Terminée' ? 0.8 : 1,
                          color: textColor
                        }}
                        title="Cliquer pour afficher/masquer la description"
                      >
                        {task.title}
                      </div>
                      <div className="taskboard-card-icons" style={{ color: iconColor }}>
                        {task.completed && <i className="bi bi-check-circle-fill" style={{ color: isTaskCompleted ? '#155724' : '#10b981' }}></i>}
                        <i 
                          className="bi bi-info-circle" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTaskInfo(task);
                          }}
                          title="Voir les informations"
                          style={{ color: iconColor }}
                        ></i>
                        <i 
                          className="bi bi-pencil-square" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditTask(task);
                          }}
                          title="Modifier"
                          style={{ color: iconColor }}
                        ></i>
                        <i 
                          className="bi bi-trash" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTask(task);
                          }}
                          title="Supprimer"
                          style={{ color: iconColor }}
                        ></i>
                      </div>
                    </div>
                    
                    {task.subTasks && task.subTasks.length > 0 && !expandedTasks.has(task.uid || task.id) && (
                      <div style={{ 
                        marginTop: '8px',
                        marginBottom: '8px'
                      }}>
                        {(() => {
                          // Utiliser UNIQUEMENT le statut pour déterminer si une sous-tâche est terminée
                          const completedCount = task.subTasks.filter(st => st.status === 'Terminée').length;
                          const totalCount = task.subTasks.length;
                          const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
                          
                          return (
                            <>
                              {/* Afficher le texte "X/Y (Z%)" dans l'aperçu */}
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                marginBottom: '4px'
                              }}>
                                <div style={{ 
                                  fontSize: '0.75rem', 
                                  fontWeight: '600', 
                                  color: textColor,
                                  fontFamily: "'Poppins', sans-serif"
                                }}>
                                  Sous-tâches :
                                </div>
                                <div style={{ 
                                  fontSize: '0.7rem', 
                                  color: metaTextColor,
                                  fontWeight: '500',
                                  fontFamily: "'Poppins', sans-serif"
                                }}>
                                  {completedCount}/{totalCount} ({percentage}%)
                                </div>
                              </div>
                              {/* Barre de progression */}
                              <div style={{ 
                                width: '100%',
                                height: '4px',
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '2px',
                                overflow: 'hidden'
                              }}>
                                <div style={{ 
                                  width: `${percentage}%`,
                                  height: '100%',
                                  backgroundColor: percentage === 100 ? '#10b981' : '#007bff',
                                  borderRadius: '2px',
                                  transition: 'width 0.3s ease'
                                }}></div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                    
                    {expandedTasks.has(task.uid || task.id) && (
                      <>
                        {task.description && (
                          <div className="taskboard-card-description" style={{ color: descriptionColor }}>{task.description}</div>
                        )}
                        {task.subTasks && task.subTasks.length > 0 && (
                          <div style={{ 
                            marginTop: task.description ? '12px' : '0',
                            paddingTop: task.description ? '12px' : '0',
                            borderTop: task.description ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'
                          }}>
                            {(() => {
                              // Utiliser UNIQUEMENT le statut pour déterminer si une sous-tâche est terminée
                              const completedCount = task.subTasks.filter(st => st.status === 'Terminée').length;
                              const totalCount = task.subTasks.length;
                              const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
                              
                              return (
                                <>
                                  {task.subTaskMode === "Collectif" && (
                                    <div style={{ marginBottom: '12px' }}>
                                      <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        marginBottom: '4px'
                                      }}>
                                        <div style={{ 
                                          fontSize: '0.85rem', 
                                          fontWeight: '600', 
                                          color: textColor,
                                          fontFamily: "'Poppins', sans-serif"
                                        }}>
                                          Sous-tâches :
                                        </div>
                                        <div style={{ 
                                          fontSize: '0.8rem', 
                                          color: metaTextColor,
                                          fontWeight: '500',
                                          fontFamily: "'Poppins', sans-serif"
                                        }}>
                                          {completedCount}/{totalCount} ({percentage}%)
                                        </div>
                                      </div>
                                      <div style={{ 
                                        width: '100%',
                                        height: '6px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        borderRadius: '3px',
                                        overflow: 'hidden',
                                        marginBottom: '12px'
                                      }}>
                                        <div style={{ 
                                          width: `${percentage}%`,
                                          height: '100%',
                                          backgroundColor: percentage === 100 ? '#10b981' : '#007bff',
                                          borderRadius: '3px',
                                          transition: 'width 0.3s ease'
                                        }}></div>
                                      </div>
                                    </div>
                                  )}
                                  {task.subTaskMode === "Individuel" && (
                                    <div style={{ marginBottom: '12px' }}>
                                      <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        marginBottom: '4px'
                                      }}>
                                        <div style={{ 
                                          fontSize: '0.85rem', 
                                          fontWeight: '600', 
                                          color: textColor,
                                          fontFamily: "'Poppins', sans-serif"
                                        }}>
                                          Sous-tâches :
                                        </div>
                                        <div style={{ 
                                          fontSize: '0.8rem', 
                                          color: metaTextColor,
                                          fontWeight: '500',
                                          fontFamily: "'Poppins', sans-serif"
                                        }}>
                                          {completedCount}/{totalCount} ({percentage}%)
                                        </div>
                                      </div>
                                      <div style={{ 
                                        width: '100%',
                                        height: '6px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        borderRadius: '3px',
                                        overflow: 'hidden',
                                        marginBottom: '12px'
                                      }}>
                                        <div style={{ 
                                          width: `${percentage}%`,
                                          height: '100%',
                                          backgroundColor: percentage === 100 ? '#10b981' : '#007bff',
                                          borderRadius: '3px',
                                          transition: 'width 0.3s ease'
                                        }}></div>
                                      </div>
                                    </div>
                                  )}
                                  {(!task.subTaskMode || (task.subTaskMode !== "Collectif" && task.subTaskMode !== "Individuel")) && (
                                    <div style={{ marginBottom: '12px' }}>
                                      <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        marginBottom: '4px'
                                      }}>
                                        <div style={{ 
                                          fontSize: '0.85rem', 
                                          fontWeight: '600', 
                                          color: textColor,
                                          fontFamily: "'Poppins', sans-serif"
                                        }}>
                                          Sous-tâches :
                                        </div>
                                        <div style={{ 
                                          fontSize: '0.8rem', 
                                          color: metaTextColor,
                                          fontWeight: '500',
                                          fontFamily: "'Poppins', sans-serif"
                                        }}>
                                          {completedCount}/{totalCount} ({percentage}%)
                                        </div>
                                      </div>
                                      <div style={{ 
                                        width: '100%',
                                        height: '6px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        borderRadius: '3px',
                                        overflow: 'hidden',
                                        marginBottom: '12px'
                                      }}>
                                        <div style={{ 
                                          width: `${percentage}%`,
                                          height: '100%',
                                          backgroundColor: percentage === 100 ? '#10b981' : '#007bff',
                                          borderRadius: '3px',
                                          transition: 'width 0.3s ease'
                                        }}></div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                            {task.subTasks
                              .filter(subTask => {
                                // Afficher la sous-tâche si son statut n'est PAS "Terminée"
                                // Afficher aussi les sous-tâches avec status null ou undefined (considérées comme non terminées)
                                return subTask.status !== 'Terminée';
                              })
                              .sort((a, b) => {
                                const orderA = a.order !== undefined && a.order !== null ? a.order : 999999;
                                const orderB = b.order !== undefined && b.order !== null ? b.order : 999999;
                                return orderA - orderB;
                              })
                              .map((subTask) => (
                              <div 
                                key={subTask.id}
                                className="subtask-item"
                                data-subtask={subTask.id}
                                draggable
                                onDragStart={(e) => {
                                  e.stopPropagation();
                                  handleSubTaskDragStart(e, task, subTask);
                                }}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSubTaskDragOver(e, subTask.id);
                                }}
                                onDragLeave={(e) => {
                                  e.stopPropagation();
                                  handleSubTaskDragLeave(e);
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSubTaskDrop(e, task, subTask.id);
                                }}
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '8px',
                                  marginBottom: '6px',
                                  fontSize: '0.75rem',
                                  cursor: 'grab',
                                  opacity: draggedSubTask?.subTask.id === subTask.id ? 0.5 : 1,
                                  borderTop: draggedOverSubTask === subTask.id ? '2px solid #007bff' : 'none',
                                  paddingTop: draggedOverSubTask === subTask.id ? '4px' : '0',
                                  marginTop: draggedOverSubTask === subTask.id ? '2px' : '0'
                                }}
                              >
                                <i 
                                  className="bi bi-grip-vertical" 
                                  style={{ 
                                    color: '#9ca3af',
                                    fontSize: '14px',
                                    cursor: 'grab',
                                    flexShrink: 0,
                                    marginRight: '4px'
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  title="Glisser pour réordonner"
                                ></i>
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleSubTaskStatus(task, subTask.id);
                                  }}
                                  style={{ 
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '18px',
                                    height: '18px',
                                    flexShrink: 0
                                  }}
                                  title={subTask.status === 'Terminée' ? 'Marquer comme En attente' : 'Marquer comme Terminée'}
                                >
                                  {subTask.status === 'Terminée' ? (
                                    <i className="bi bi-check-square-fill" style={{ color: checkboxCheckedColor, fontSize: '18px' }}></i>
                                  ) : (
                                    <i className="bi bi-square" style={{ color: checkboxColor, fontSize: '18px' }}></i>
                                  )}
                                </div>
                                <span style={{ 
                                  textDecoration: subTask.status === 'Terminée' ? 'line-through' : 'none',
                                  opacity: subTask.status === 'Terminée' ? 0.8 : 1,
                                  color: textColor,
                                  flex: 1,
                                  fontSize: '0.85rem',
                                  fontFamily: "'Poppins', sans-serif"
                                }}>
                                  {subTask.title}
                                </span>
                                {subTask.assignedTo && task.subTaskMode === "Collectif" && (
                                  <div 
                                    className="taskboard-card-badge" 
                                    title={subTask.assignedTo}
                                    style={{ 
                                      marginLeft: 'auto',
                                      flexShrink: 0
                                    }}
                                  >
                                    {subTask.assignedTo.substring(0, 2).toUpperCase()}
                                  </div>
                                )}
                                {task.subTaskMode === "Individuel" && task.assignedTo && (
                                  <div 
                                    className="taskboard-card-badge" 
                                    title={task.assignedTo}
                                    style={{ 
                                      marginLeft: 'auto',
                                      flexShrink: 0
                                    }}
                                  >
                                    {task.assignedTo.substring(0, 2).toUpperCase()}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    
                    <div className="taskboard-card-footer">
                      <div className="taskboard-card-meta">
                        {(!task.subTasks || task.subTasks.length === 0) && (
                          <div 
                            className="taskboard-card-checkbox" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleTaskStatus(task);
                            }}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                            title={task.status === 'Terminée' ? 'Marquer comme En cours' : 'Marquer comme Terminée'}
                          >
                            {task.status === 'Terminée' ? (
                              <i className="bi bi-check-square-fill" style={{ color: checkboxCheckedColor, fontSize: '16px' }}></i>
                            ) : (
                              <i className="bi bi-square" style={{ fontSize: '16px', color: checkboxColor }}></i>
                            )}
                          </div>
                        )}
                        {task.subTasks && task.subTasks.length > 0 && (
                          <div className="taskboard-card-checkbox" style={{ marginLeft: '0', display: 'flex', alignItems: 'center', gap: '4px', color: metaTextColor }}>
                            <i className="bi bi-check-square" style={{ fontSize: '16px', color: checkboxColor }}></i>
                            <span style={{ color: metaTextColor }}>{task.completedItems || 0}/{task.totalItems || task.subTasks.length}</span>
                          </div>
                        )}
                        {task.assignedTo && task.subTaskMode !== "Collectif" && (
                          <div className="taskboard-card-badge" title={task.assignedTo}>
                            {task.assignedTo.substring(0, 2).toUpperCase()}
                          </div>
                        )}
                      </div>
                        {task.priority && (
                        <span style={{ 
                          fontSize: '0.8rem', 
                          color: isTaskCompleted 
                            ? (task.priority === 'Haute' ? '#721c24' : task.priority === 'Moyenne' ? '#856404' : '#155724')
                            : (task.priority === 'Haute' ? '#ef4444' : task.priority === 'Moyenne' ? '#f59e0b' : '#6b7280'),
                          fontFamily: "'Poppins', sans-serif"
                        }}>
                          {task.priority}
                        </span>
                      )}
                    </div>
                  </div>
                  );
                })}
                
                {draggedOverList === list.id && (
                  <div className="taskboard-drop-zone drag-over">
                    Déposer ici
                  </div>
                )}
                
                <button 
                  className="taskboard-add-card-button"
                  onClick={() => handleAddTask(list.id)}
                >
                  <i className="bi bi-plus"></i>
                  Ajouter une carte
                </button>
              </div>
            </div>
          );
        })}
        
        {/* Section 2: Tâches terminées - Carte avec défilement */}
        <div style={{
          width: '600px',
          minWidth: '600px',
          backgroundColor: 'transparent',
          paddingLeft: '0',
          paddingRight: '0',
          marginRight: '0',
          marginLeft: '0',
          flexShrink: 0
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 300px)',
            maxHeight: 'calc(100vh - 300px)',
            width: '100%',
            marginRight: '0'
          }}>
            <div style={{
              padding: '16px',
              borderBottom: '1px solid #e5e7eb',
              flexShrink: 0
            }}>
              <h3 style={{
                fontSize: '1.15rem',
                fontWeight: '600',
                color: '#172b4d',
                margin: 0,
                fontFamily: "'Poppins', sans-serif"
              }}>
                Tâches terminées
              </h3>
            </div>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0',
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              padding: '12px'
            }}>
            {completedTasks.length > 0 ? (
              completedTasks.map((item, index) => {
                const formatDate = (timestamp?: Timestamp) => {
                  if (!timestamp) return '';
                  const date = timestamp.toDate();
                  const now = new Date();
                  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
                  
                  if (diffDays === 0) return "Aujourd'hui";
                  if (diffDays === 1) return "Hier";
                  if (diffDays < 7) return `Il y a ${diffDays} jours`;
                  
                  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                };
                
                return (
                  <div
                    key={item.id + '-' + index}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0',
                      padding: '6px 0',
                      cursor: 'pointer',
                      borderBottom: index < completedTasks.length - 1 ? '1px solid #e5e7eb' : 'none',
                      paddingBottom: '6px'
                    }}
                    onClick={() => item.task && handleViewTaskInfo(item.task)}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      flexWrap: 'nowrap'
                    }}>
                      <i className="bi bi-check-square-fill" style={{
                        color: '#10b981',
                        fontSize: '14px',
                        flexShrink: 0
                      }}></i>
                      {item.assignedTo && (
                        <div 
                          className="taskboard-card-badge" 
                          title={item.assignedTo}
                          style={{
                            flexShrink: 0,
                            width: '22px',
                            height: '22px',
                            fontSize: '0.6rem'
                          }}
                        >
                          {item.assignedTo.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <span style={{
                        fontSize: '0.9rem',
                        color: '#6b7280',
                        textDecoration: 'line-through',
                        flex: 1,
                        fontWeight: item.isSubTask ? '400' : '500',
                        minWidth: 0,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: '1.4',
                        wordBreak: 'break-word',
                        fontFamily: "'Poppins', sans-serif"
                      }}>
                        {item.title}
                        {item.isSubTask && item.parentTaskTitle && (
                          <span style={{
                            fontSize: '0.7rem',
                            color: '#9ca3af',
                            fontStyle: 'italic',
                            marginLeft: '6px'
                          }}>
                            ({item.parentTaskTitle})
                          </span>
                        )}
                      </span>
                      {item.completedAt && (
                        <span style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '0.75rem',
                          color: '#9ca3af',
                          flexShrink: 0,
                          marginLeft: '8px'
                        }}>
                          <i className="bi bi-calendar-check" style={{ fontSize: '12px' }}></i>
                          {formatDate(item.completedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              }              )
            ) : (
              <div style={{
                color: '#9ca3af',
                fontSize: '0.85rem',
                padding: '8px 0',
                textAlign: 'center'
              }}>
                Aucune tâche terminée
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      <TaskModal
        show={showModal}
        handleClose={() => {
          setShowModal(false);
          setTaskToEdit(null);
        }}
        initialData={taskToEdit}
      />

      {/* Modale d'information de la tâche */}
      {showInfoModal && taskToView && (
        <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ borderRadius: '12px', display: 'flex', flexDirection: 'column', maxHeight: '90vh', padding: 0, maxWidth: '600px' }}>
            <div className="modal-header" style={{ backgroundColor: '#DFE6E9', borderRadius: '12px 12px 0 0', padding: '1.5rem', flexShrink: 0 }}>
              <h2 style={{ fontFamily: 'Montserrat, sans-serif', margin: 0, fontWeight: 'bold', color: '#424273' }}>Informations de la tâche</h2>
              <button className="modal-close" onClick={() => setShowInfoModal(false)}>×</button>
            </div>
            
            <div style={{ overflowY: 'auto', flex: 1 }}>
              <div className="form-group inter-font" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '1.5rem' }}>
                <label style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>Titre</label>
                <div style={{ fontSize: '0.85rem', color: '#333', marginTop: '4px' }}>{taskToView.title}</div>
              </div>

              {taskToView.description && (
                <div className="form-group inter-font" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '1.5rem' }}>
                  <label style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>Description</label>
                  <div style={{ fontSize: '0.85rem', color: '#333', marginTop: '4px', whiteSpace: 'pre-wrap' }}>{taskToView.description}</div>
                </div>
              )}

              <div className="form-group inter-font" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '1.5rem' }}>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>Statut</label>
                    <div style={{ fontSize: '0.85rem', color: '#333', marginTop: '4px' }}>{taskToView.status}</div>
                  </div>

                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>Priorité</label>
                    <div style={{ fontSize: '0.85rem', color: '#333', marginTop: '4px' }}>{taskToView.priority}</div>
                  </div>
                </div>
              </div>

              {taskToView.subTaskMode === "Individuel" && taskToView.assignedTo && (
                <div className="form-group inter-font" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '1.5rem' }}>
                  <label style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>Assigné à</label>
                  <div style={{ fontSize: '0.85rem', color: '#333', marginTop: '4px' }}>{taskToView.assignedTo}</div>
                </div>
              )}

              {taskToView.dueDate && (
                <div className="form-group inter-font" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '1.5rem' }}>
                  <label style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>Date d'échéance</label>
                  <div style={{ fontSize: '0.85rem', color: '#333', marginTop: '4px' }}>
                    {taskToView.dueDate.toDate().toLocaleDateString('fr-FR')}
                  </div>
                </div>
              )}

              {taskToView.subTaskMode && (
                <div className="form-group inter-font" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '1.5rem' }}>
                  <label style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>Mode</label>
                  <div style={{ fontSize: '0.85rem', color: '#333', marginTop: '4px' }}>{taskToView.subTaskMode}</div>
                </div>
              )}

              {taskToView.subTasks && taskToView.subTasks.length > 0 && (
                <div className="form-group inter-font" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '1.5rem' }}>
                  <label style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0, marginBottom: '8px', display: 'block' }}>Sous-tâches</label>
                  <div style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '4px', 
                    padding: '8px',
                    backgroundColor: '#f9f9f9'
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#e9ecef', borderBottom: '2px solid #ddd' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.7rem', fontWeight: '600', width: '30px' }}></th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.7rem', fontWeight: '600' }}>#</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.7rem', fontWeight: '600' }}>Titre</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.7rem', fontWeight: '600' }}>Assigné à</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.7rem', fontWeight: '600' }}>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taskToView.subTasks
                          .sort((a, b) => {
                            const orderA = a.order !== undefined && a.order !== null ? a.order : 999999;
                            const orderB = b.order !== undefined && b.order !== null ? b.order : 999999;
                            return orderA - orderB;
                          })
                          .map((subTask, index) => (
                          <tr 
                            key={subTask.id}
                            className="subtask-item"
                            data-subtask={subTask.id}
                            draggable
                            onDragStart={(e) => {
                              e.stopPropagation();
                              handleSubTaskDragStart(e, taskToView, subTask);
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSubTaskDragOver(e, subTask.id);
                            }}
                            onDragLeave={(e) => {
                              e.stopPropagation();
                              handleSubTaskDragLeave(e);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSubTaskDrop(e, taskToView, subTask.id);
                            }}
                            style={{ 
                              borderBottom: '1px solid #e0e0e0',
                              cursor: 'grab',
                              opacity: draggedSubTask?.subTask.id === subTask.id ? 0.5 : 1,
                              backgroundColor: draggedOverSubTask === subTask.id ? '#f0f8ff' : 'transparent'
                            }}
                          >
                            <td style={{ padding: '6px 8px', fontSize: '0.7rem', width: '30px' }}>
                              <i 
                                className="bi bi-grip-vertical" 
                                style={{ 
                                  color: '#9ca3af',
                                  fontSize: '14px',
                                  cursor: 'grab'
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                title="Glisser pour réordonner"
                              ></i>
                            </td>
                            <td style={{ padding: '6px 8px', fontSize: '0.7rem' }}>{index + 1}</td>
                            <td style={{ padding: '6px 8px', fontSize: '0.7rem' }}>
                              <span style={{ 
                                textDecoration: (subTask.status === 'Terminée' || subTask.completed) ? 'line-through' : 'none',
                                opacity: (subTask.status === 'Terminée' || subTask.completed) ? 0.6 : 1
                              }}>
                                {subTask.title}
                              </span>
                            </td>
                            <td style={{ padding: '6px 8px', fontSize: '0.7rem' }}>
                              {subTask.assignedTo ? (
                                <div className="taskboard-card-badge" style={{ display: 'inline-flex', margin: 0 }}>
                                  {subTask.assignedTo.substring(0, 2).toUpperCase()}
                                </div>
                              ) : (
                                <span style={{ color: '#999' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '6px 8px', fontSize: '0.7rem' }}>{subTask.status || 'En attente'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ 
                      fontSize: '0.7rem', 
                      color: '#6b778c', 
                      marginTop: '8px',
                      textAlign: 'right',
                      paddingTop: '8px',
                      borderTop: '1px solid #e0e0e0'
                    }}>
                      {taskToView.completedItems || 0}/{taskToView.totalItems || taskToView.subTasks.length} complétées
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group inter-font" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '1.5rem' }}>
                <label style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>Date de création</label>
                <div style={{ fontSize: '0.85rem', color: '#333', marginTop: '4px' }}>
                  {taskToView.createdAt?.toDate().toLocaleDateString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>

              {taskToView.updatedAt && taskToView.updatedAt.toMillis() !== taskToView.createdAt?.toMillis() && (
                <div className="form-group inter-font" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '1.5rem' }}>
                  <label style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>Dernière modification</label>
                  <div style={{ fontSize: '0.85rem', color: '#333', marginTop: '4px' }}>
                    {taskToView.updatedAt.toDate().toLocaleDateString('fr-FR', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddListModal && (
        <div className="modal-overlay" onClick={() => setShowAddListModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ borderRadius: '12px', display: 'flex', flexDirection: 'column', maxHeight: '90vh', padding: 0 }}>
            <div className="modal-header" style={{ backgroundColor: '#DFE6E9', borderRadius: '12px 12px 0 0', padding: '1.5rem', flexShrink: 0 }}>
              <h2 style={{ fontFamily: 'Montserrat, sans-serif', margin: 0, fontWeight: 'bold', color: '#424273' }}>Ajouter une liste</h2>
              <button className="modal-close" onClick={() => setShowAddListModal(false)}>×</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              <div className="form-group inter-font" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '1.5rem' }}>
                <label style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>Titre de la liste</label>
                <input
                  type="text"
                  value={newListTitle}
                  onChange={(e) => setNewListTitle(e.target.value)}
                  className="form-control inter-font"
                  placeholder="Ex: En cours"
                />
              </div>
              <div className="form-group inter-font" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '1.5rem' }}>
                <label style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>Couleur</label>
                <select
                  value={newListColor}
                  onChange={(e) => setNewListColor(e.target.value as any)}
                  className="form-control inter-font"
                >
                  <option value="orange">Orange</option>
                  <option value="green">Vert</option>
                  <option value="grey">Gris</option>
                  <option value="blue">Bleu</option>
                  <option value="purple">Violet</option>
                </select>
              </div>
            </div>
            <div className="modal-actions" style={{ padding: '0 1.5rem 1.5rem 1.5rem' }}>
              <button className="cancel-button inter-font" onClick={() => setShowAddListModal(false)} style={{ fontSize: '0.8rem' }}>
                Annuler
              </button>
              <button className="submit-button inter-font" onClick={handleAddList} style={{ fontSize: '0.8rem' }}>
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditListModal && listToEdit && (
        <div className="modal-overlay" onClick={() => {
          setShowEditListModal(false);
          setListToEdit(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Modifier la liste</h2>
              <button className="modal-close" onClick={() => {
                setShowEditListModal(false);
                setListToEdit(null);
              }}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <div className="form-group inter-font">
                <label>Titre de la liste</label>
                <input
                  type="text"
                  value={editListTitle}
                  onChange={(e) => setEditListTitle(e.target.value)}
                  className="form-control inter-font"
                  placeholder="Ex: En cours"
                />
              </div>
              <div className="form-group inter-font">
                <label>Couleur</label>
                <select
                  value={editListColor}
                  onChange={(e) => setEditListColor(e.target.value as any)}
                  className="form-control inter-font"
                >
                  <option value="orange">Orange</option>
                  <option value="green">Vert</option>
                  <option value="grey">Gris</option>
                  <option value="blue">Bleu</option>
                  <option value="purple">Violet</option>
                </select>
              </div>
              <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  {listToEdit && !['today', 'this-week', 'later'].includes(listToEdit.id) && (
                    <button 
                      className="cancel-button" 
                      onClick={handleDeleteList}
                      style={{ 
                        backgroundColor: '#ef4444', 
                        color: 'white',
                        border: 'none',
                        marginRight: '10px'
                      }}
                    >
                      <i className="bi bi-trash" style={{ marginRight: '5px' }}></i>
                      Supprimer
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button className="cancel-button" onClick={() => {
                    setShowEditListModal(false);
                    setListToEdit(null);
                  }}>
                    Annuler
                  </button>
                  <button className="submit-button" onClick={handleSaveList}>
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {taskToDelete && (
        <div className="modal-overlay">
          <div className="delete-confirmation">
            <h2>Confirmer la suppression</h2>
            <p>Êtes-vous sûr de vouloir supprimer la tâche "{taskToDelete.title}" ?</p>
            <p className="warning-text">Cette action est irréversible.</p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={cancelDeleteTask}>
                Annuler
              </button>
              <button className="delete-button" onClick={confirmDeleteTask}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GIF de célébration quand une carte passe à "Terminée" */}
      {showCelebrationGif && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 10000,
            pointerEvents: 'none'
          }}
        >
          <img
            src="/linksawakening.gif"
            alt="Célébration"
            style={{
              width: '200px',
              height: 'auto',
              display: 'block'
            }}
          />
        </div>
      )}

      <Footer />
    </div>
  );
};

export default TachesAdministration;
